import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { OptionsSyncButton } from './OptionsSyncButton';

export const OptionsDashboardControls = ({
  onSync,
  syncing,
  environment,
  lastSyncAt,
  showManageConnection = true,
  showSyncButton = true,
}) => {
  const { t } = useOptionsLocale();
  const syncLabel = buildSyncLabel(lastSyncAt, t);

  if (!showManageConnection && !showSyncButton) {
    return null;
  }

  return (
    <section className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.045),rgba(255,255,255,0.02))] px-4 py-3 sm:px-5" data-testid="options-dashboard-controls">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 space-y-1" data-testid="options-dashboard-controls-status">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/82" data-testid="options-dashboard-controls-primary-line">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#00E676]" data-testid="options-dashboard-controls-connected-dot" />
            <span className="font-semibold uppercase tracking-[0.16em] text-white/60" data-testid="options-dashboard-controls-provider">Deribit</span>
            <span className="text-white/35">·</span>
            <span className="font-medium text-white/88" data-testid="options-dashboard-controls-environment">{environment === 'testnet' ? t('common.testnet') : t('common.production')}</span>
            {syncLabel ? <span className="text-white/35">·</span> : null}
            {syncLabel ? <span className="text-white/58" data-testid="options-dashboard-controls-sync-status">{syncLabel}</span> : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3" data-testid="options-dashboard-controls-actions">
          {showManageConnection ? (
            <Button asChild size="sm" variant="outline" className="rounded-full border-white/12 bg-white/[0.03] px-4 text-white hover:bg-white/8 hover:text-white" data-testid="options-dashboard-manage-connection-button">
              <Link to="/options/connect">{t('common.manageConnection')}</Link>
            </Button>
          ) : null}

          {showSyncButton ? (
            <OptionsSyncButton onSync={onSync} syncing={syncing} compact testId="options-dashboard-sync-button" />
          ) : null}
        </div>
      </div>
    </section>
  );
};

const buildSyncLabel = (lastSyncAt, t) => {
  if (!lastSyncAt) {
    return null;
  }

  const syncDate = new Date(lastSyncAt);
  if (Number.isNaN(syncDate.getTime())) {
    return null;
  }

  const diffMs = Date.now() - syncDate.getTime();
  if (diffMs < 60_000) {
    return t('dashboard.syncedJustNow');
  }

  const diffMinutes = Math.round(diffMs / 60_000);
  if (diffMinutes < 60) {
    return t('dashboard.syncedMinutesAgo').replace('{count}', String(diffMinutes));
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return t('dashboard.syncedHoursAgo').replace('{count}', String(diffHours));
  }

  return t('dashboard.lastSyncAt').replace(
    '{time}',
    new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    }).format(syncDate),
  );
};

export default OptionsDashboardControls;